import { useEffect, useMemo, useState } from "react";
import { Search, Star, CalendarCheck, Download, X, Clock, MapPin, IndianRupee, SlidersHorizontal, FileSpreadsheet, Timer, Loader2, AlertCircle, Pencil, Trash2 } from "lucide-react";
import { getCourses, createCourse, updateCourse, deleteCourse } from "../service/courseService.jsx";

// Change this if expertId should come from auth/context instead of being fixed.
const EXPERT_ID = 1;

const BASE_CATEGORIES = ["Manufacturing", "Robotics", "Electronics", "Automotive"];

// ---------------------------------------------------------------------------
// Map an API course record -> the flat shape the UI already expects
// ---------------------------------------------------------------------------
function normalizeCourse(c) {
    return {
        id: c.id,
        expertId: c.expertId,
        name: c.mentorName,
        initials: getInitials(c.mentorName || ""),
        expertise: c.expertise,
        institution: c.institution,
        category: c.category,
        timings: c.timings,
        duration: c.sessionDuration != null ? `${c.sessionDuration} min` : "—",
        sessionDuration: c.sessionDuration,
        price: Number(c.price) || 0,
        rating: Number(c.rating) || 0,
        experience: c.experienceYears != null ? `${c.experienceYears} yrs` : "—",
        experienceYears: c.experienceYears,
        location: c.location,
        description: c.description,
        active: c.active,
    };
}

// ---------------------------------------------------------------------------
// CSV export helper
// ---------------------------------------------------------------------------
function exportToCSV(rows) {
    const headers = [
        "Name",
        "Expertise",
        "Institution",
        "Category",
        "Timings",
        "Duration",
        "Rating",
        "Price (INR)",
        "Experience",
        "Location",
    ];
    const lines = rows.map((r) =>
        [r.name, r.expertise, r.institution, r.category, r.timings, r.duration, r.rating, r.price, r.experience, r.location]
            .map((v) => `"${String(v).replace(/"/g, '""')}"`)
            .join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `mentor-directory-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Booking popup
// ---------------------------------------------------------------------------
const SLOTS = ["10:00 AM", "11:30 AM", "2:00 PM", "4:00 PM", "6:00 PM"];

function BookingModal({ mentor, onClose }) {
    const [date, setDate] = useState("");
    const [slot, setSlot] = useState(SLOTS[0]);
    const [confirmed, setConfirmed] = useState(false);

    if (!mentor) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-semibold">
                            {mentor.initials}
                        </div>
                        <div>
                            <p className="font-semibold text-slate-900 leading-tight">{mentor.name}</p>
                            <p className="text-sm text-slate-500">{mentor.expertise}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {confirmed ? (
                    <div className="p-6 text-center space-y-2">
                        <CalendarCheck className="h-10 w-10 text-emerald-500 mx-auto" />
                        <p className="font-semibold text-slate-900">Session booked</p>
                        <p className="text-sm text-slate-500">
                            {mentor.name} · {date || "date TBD"} · {slot}
                        </p>
                        <button
                            onClick={onClose}
                            className="mt-3 w-full rounded-lg bg-slate-900 text-white py-2 text-sm font-medium hover:bg-slate-800"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="p-5 space-y-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-slate-400 text-xs">Institution</p>
                                <p className="text-slate-700 font-medium">{mentor.institution}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-slate-400 text-xs">Rating</p>
                                <p className="text-slate-700 font-medium flex items-center gap-1">
                                    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {mentor.rating}
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Pick a date</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Pick a time slot</label>
                            <div className="mt-1 flex flex-wrap gap-2">
                                {SLOTS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSlot(s)}
                                        className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${slot === s
                                            ? "bg-indigo-600 border-indigo-600 text-white"
                                            : "border-slate-200 text-slate-600 hover:border-indigo-300"
                                            }`}
                                    >
                                        <Clock className="h-3 w-3" />
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-between rounded-lg bg-indigo-50 px-3 py-2">
                            <span className="text-sm text-slate-600">Session fee</span>
                            <span className="flex items-center font-semibold text-indigo-700">
                                <IndianRupee className="h-3.5 w-3.5" />
                                {mentor.price.toLocaleString("en-IN")}
                            </span>
                        </div>

                        <button
                            disabled={!date}
                            onClick={() => setConfirmed(true)}
                            className="w-full rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-2.5 text-sm font-medium shadow-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                        >
                            Confirm booking
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Export popup
// ---------------------------------------------------------------------------
function ExportModal({ rows, onClose }) {
    const [downloaded, setDownloaded] = useState(false);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-slate-100 p-5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
                            <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-slate-900">Export course details</p>
                            <p className="text-xs text-slate-500">{rows.length} course{rows.length !== 1 ? "s" : ""} ready to export</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="overflow-auto flex-1">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50/95 backdrop-blur">
                            <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                <th className="px-4 py-3">Course / Mentor</th>
                                <th className="px-4 py-3">Timings</th>
                                <th className="px-4 py-3">Duration</th>
                                <th className="px-4 py-3">Rating</th>
                                <th className="px-4 py-3">Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-slate-50 last:border-0">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-slate-900">{r.name}</p>
                                        <p className="text-xs text-slate-500">{r.expertise} · {r.institution}</p>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <span className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5 text-slate-400" />
                                            {r.timings}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <span className="flex items-center gap-1.5">
                                            <Timer className="h-3.5 w-3.5 text-slate-400" />
                                            {r.duration}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="flex items-center gap-1 font-medium text-slate-700">
                                            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {r.rating}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="flex items-center font-semibold text-indigo-600">
                                            <IndianRupee className="h-3.5 w-3.5" />
                                            {r.price.toLocaleString("en-IN")}
                                        </span>
                                    </td>
                                </tr>
                            ))}

                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">
                                        No courses to export — adjust your filters first.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between border-t border-slate-100 p-4">
                    <p className="text-xs text-slate-400">Exports the courses currently shown, exactly as previewed above.</p>
                    <button
                        disabled={rows.length === 0}
                        onClick={() => {
                            exportToCSV(rows);
                            setDownloaded(true);
                        }}
                        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    >
                        <Download className="h-4 w-4" />
                        {downloaded ? "Downloaded — export again" : "Download CSV"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Delete confirmation popup
// ---------------------------------------------------------------------------
function DeleteConfirmModal({ mentor, onClose, onConfirm, deleting, deleteError }) {
    if (!mentor) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={() => !deleting && onClose()}
        >
            <div
                className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50">
                            <Trash2 className="h-5 w-5 text-rose-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-slate-900">Delete this course?</p>
                            <p className="text-xs text-slate-500">This can't be undone.</p>
                        </div>
                    </div>

                    <div className="rounded-lg bg-slate-50 p-3 text-sm">
                        <p className="font-medium text-slate-900">{mentor.name}</p>
                        <p className="text-slate-500">{mentor.expertise}</p>
                    </div>

                    {deleteError && (
                        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600">{deleteError}</p>
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={deleting}
                            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={deleting}
                            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-rose-700 transition-colors disabled:opacity-50"
                        >
                            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            {deleting ? "Deleting..." : "Delete"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Add / Edit course popup — posts or puts to the API on submit.
// ---------------------------------------------------------------------------
const emptyForm = {
    name: "",
    expertise: "",
    institution: "",
    category: "",
    timings: "",
    duration: "",
    price: "",
    rating: "",
    experience: "",
    location: "",
    description: "",
};

function getInitials(name) {
    const parts = name
        .replace(/^(Dr\.|Prof\.|Mr\.|Ms\.|Mrs\.)\s*/i, "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) return "??";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Turn a normalized mentor row back into form field values, for editing.
function courseToForm(mentor) {
    if (!mentor) return emptyForm;
    return {
        name: mentor.name || "",
        expertise: mentor.expertise || "",
        institution: mentor.institution || "",
        category: mentor.category || "",
        timings: mentor.timings || "",
        duration: mentor.sessionDuration != null ? String(mentor.sessionDuration) : "",
        price: mentor.price != null ? String(mentor.price) : "",
        rating: mentor.rating != null ? String(mentor.rating) : "",
        experience: mentor.experienceYears != null ? String(mentor.experienceYears) : "",
        location: mentor.location || "",
        description: mentor.description || "",
    };
}

function CourseFormModal({ categories, expertId, mentor, onClose, onSave, saving, saveError }) {
    const isEdit = Boolean(mentor);
    const [form, setForm] = useState(() => courseToForm(mentor));
    const [error, setError] = useState("");

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.expertise || !form.price || !form.timings) {
            setError("Name, expertise, timings and price are required.");
            return;
        }
        setError("");

        // Map the form -> the API's exact body shape (same for POST and PUT).
        const payload = {
            expertId,
            mentorName: form.name,
            expertise: form.expertise,
            institution: form.institution || "—",
            category: form.category || "General",
            timings: form.timings,
            sessionDuration: form.duration ? Number(form.duration) : 0,
            price: Number(form.price) || 0,
            rating: form.rating ? Number(form.rating) : 0,
            experienceYears: form.experience ? Number(form.experience) : 0,
            location: form.location || "—",
            description: form.description || "",
        };

        onSave(payload, isEdit ? mentor.id : null);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-slate-100 p-5">
                    <div>
                        <p className="font-semibold text-slate-900">{isEdit ? "Edit course" : "Add a course"}</p>
                        <p className="text-xs text-slate-500">
                            {isEdit ? "Update the details — changes save on submit." : "Fill in the details — it'll appear in the table right away."}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {(error || saveError) && (
                        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600">{error || saveError}</p>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Mentor / instructor name *</label>
                            <input
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Dr. Ananya Reddy"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Course / expertise *</label>
                            <input
                                value={form.expertise}
                                onChange={update("expertise")}
                                placeholder="Additive Manufacturing"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Institution</label>
                            <input
                                value={form.institution}
                                onChange={update("institution")}
                                placeholder="IIT Tirupati"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Category</label>
                            <input
                                list="category-options"
                                value={form.category}
                                onChange={update("category")}
                                placeholder="Manufacturing"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                            <datalist id="category-options">
                                {categories.map((c) => (
                                    <option key={c} value={c} />
                                ))}
                            </datalist>
                        </div>

                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Timings *</label>
                            <input
                                value={form.timings}
                                onChange={update("timings")}
                                placeholder="Mon & Wed, 6:00 PM - 7:00 PM"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Session duration (min)</label>
                            <input
                                type="number"
                                min="0"
                                value={form.duration}
                                onChange={update("duration")}
                                placeholder="60"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Price (INR) *</label>
                            <input
                                type="number"
                                min="0"
                                value={form.price}
                                onChange={update("price")}
                                placeholder="1500"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Experience (years)</label>
                            <input
                                type="number"
                                min="0"
                                value={form.experience}
                                onChange={update("experience")}
                                placeholder="12"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Location</label>
                            <input
                                value={form.location}
                                onChange={update("location")}
                                placeholder="Tirupati, AP"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Description</label>
                            <textarea
                                value={form.description}
                                onChange={update("description")}
                                placeholder="Short description of the course/session"
                                rows={3}
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:opacity-95 transition-opacity disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
                        {saving ? "Saving..." : isEdit ? "Update course" : "Save course"}
                    </button>
                </div>
            </form>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MentorDirectory() {
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All");
    const [minRating, setMinRating] = useState(0);
    const [maxPrice, setMaxPrice] = useState(3000);
    const [sortKey, setSortKey] = useState("rating");
    const [activeMentor, setActiveMentor] = useState(null);
    const [exportOpen, setExportOpen] = useState(false);

    // Add / Edit form state
    const [formOpen, setFormOpen] = useState(false);
    const [editingMentor, setEditingMentor] = useState(null); // null = add mode
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Delete state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const loadCourses = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const response = await getCourses(EXPERT_ID);
            const list = Array.isArray(response) ? response : response?.data || [];
            setMentors(list.map(normalizeCourse));
        } catch (err) {
            console.error("Failed to fetch courses:", err);
            setLoadError(err.message || "Failed to load courses. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const categories = useMemo(
        () => Array.from(new Set([...BASE_CATEGORIES, ...mentors.map((m) => m.category)])),
        [mentors]
    );

    const filtered = useMemo(() => {
        let rows = mentors.filter((m) => {
            const matchesSearch =
                (m.name || "").toLowerCase().includes(search.toLowerCase()) ||
                (m.expertise || "").toLowerCase().includes(search.toLowerCase()) ||
                (m.institution || "").toLowerCase().includes(search.toLowerCase());
            const matchesCategory = category === "All" || m.category === category;
            const matchesRating = m.rating >= minRating;
            const matchesPrice = m.price <= maxPrice;
            return matchesSearch && matchesCategory && matchesRating && matchesPrice;
        });

        rows = rows.sort((a, b) => {
            if (sortKey === "rating") return b.rating - a.rating;
            if (sortKey === "price_low") return a.price - b.price;
            if (sortKey === "price_high") return b.price - a.price;
            return (a.name || "").localeCompare(b.name || "");
        });

        return rows;
    }, [mentors, search, category, minRating, maxPrice, sortKey]);

    const openAddForm = () => {
        setEditingMentor(null);
        setSaveError(null);
        setFormOpen(true);
    };

    const openEditForm = (mentor) => {
        setEditingMentor(mentor);
        setSaveError(null);
        setFormOpen(true);
    };

    // payload is the shared POST/PUT body shape; courseId is non-null on edit.
    const handleSaveCourse = async (payload, courseId) => {
        try {
            setSaving(true);
            setSaveError(null);
            if (courseId) {
                await updateCourse(courseId, payload);
            } else {
                await createCourse(payload);
            }
            setFormOpen(false);
            setEditingMentor(null);
            await loadCourses(); // refresh table
        } catch (err) {
            console.error("Failed to save course:", err);
            setSaveError(err.message || "Failed to save course. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCourse = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            setDeleteError(null);
            console.log("Deleting course id:", deleteTarget.id); // confirm the id is correct
            const result = await deleteCourse(deleteTarget.id);
            console.log("Delete response:", result); // confirm what backend actually returned
            setDeleteTarget(null);
            await loadCourses();
        } catch (err) {
            console.error("Failed to delete course:", err);
            setDeleteError(err.message || "Failed to delete course. Please try again.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
            <div className="mx-auto max-w-6xl">
                {/* Header */}
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Mentor Directory</h1>
                        <p className="text-sm text-slate-500">
                            {loading ? "Loading courses..." : `${filtered.length} of ${mentors.length} mentors match your filters`}
                        </p>
                    </div>
                    <div className="flex gap-2 self-start">
                        <button
                            onClick={openAddForm}
                            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-2 text-sm font-medium shadow-sm hover:opacity-95 transition-opacity"
                        >
                            <CalendarCheck className="h-4 w-4" />
                            Add
                        </button>
                        <button
                            onClick={() => setExportOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors"
                        >
                            <Download className="h-4 w-4" />
                            Export
                        </button>
                    </div>
                </div>

                {loadError && (
                    <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                        <AlertCircle size={20} />
                        <span>{loadError}</span>
                    </div>
                )}

                {/* Filters */}
                <div className="mb-6 grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="relative lg:col-span-2">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, expertise, institution..."
                            disabled={loading}
                            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50"
                        />
                    </div>

                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        disabled={loading}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50"
                    >
                        {["All", ...categories].map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>




                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    <th className="px-4 py-3">Mentor</th>
                                    <th className="px-4 py-3">Expertise</th>
                                    <th className="px-4 py-3">Institution</th>
                                    <th className="px-4 py-3">Category</th>
                                    <th className="px-4 py-3">Timings</th>

                                    <th className="px-4 py-3">Price</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 size={18} className="animate-spin" />
                                                Loading courses...
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    filtered.map((m) => (
                                        <tr key={m.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-xs font-semibold text-white">
                                                        {m.initials}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{m.name}</p>
                                                        <p className="flex items-center gap-1 text-xs text-slate-400">
                                                            <MapPin className="h-3 w-3" /> {m.location}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">{m.expertise}</td>
                                            <td className="px-4 py-3 text-slate-600">{m.institution}</td>
                                            <td className="px-4 py-3">
                                                <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
                                                    {m.category}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-500">
                                                <span className="flex items-center gap-1.5">
                                                    <Clock className="h-3.5 w-3.5 text-slate-400" />
                                                    {m.timings}
                                                </span>
                                            </td>

                                            <td className="px-4 py-3">
                                                <span className="flex items-center font-semibold text-indigo-600">
                                                    <IndianRupee className="h-3.5 w-3.5" />
                                                    {m.price.toLocaleString("en-IN")}
                                                    <span className="ml-1 font-normal text-slate-400">/session</span>
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => openEditForm(m)}
                                                        className="rounded-lg p-2 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                                                        aria-label={`Edit ${m.name}`}
                                                        title="Edit"
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setDeleteError(null);
                                                            setDeleteTarget(m);
                                                        }}
                                                        className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                                                        aria-label={`Delete ${m.name}`}
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}

                                {!loading && !loadError && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">
                                            No mentors match these filters. Try widening your search.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <BookingModal mentor={activeMentor} onClose={() => setActiveMentor(null)} />
            {exportOpen && <ExportModal rows={filtered} onClose={() => setExportOpen(false)} />}
            {formOpen && (
                <CourseFormModal
                    categories={categories}
                    expertId={EXPERT_ID}
                    mentor={editingMentor}
                    saving={saving}
                    saveError={saveError}
                    onClose={() => {
                        if (saving) return;
                        setFormOpen(false);
                        setEditingMentor(null);
                    }}
                    onSave={handleSaveCourse}
                />
            )}
            <DeleteConfirmModal
                mentor={deleteTarget}
                deleting={deleting}
                deleteError={deleteError}
                onClose={() => !deleting && setDeleteTarget(null)}
                onConfirm={handleDeleteCourse}
            />
        </div>
    );
}